import { For, Show } from "solid-js";
import { draftPattern, type DraftBlock, type DraftResult, type PatternSpecification } from "@tailor-frame/engine";
import { defaultsFor } from "@tailor-frame/patterns";
import { pieceColourCss } from "../core/pieceColours";
import { packBoxes } from "../core/pieceLayout";
import { segmentPath } from "./PrintPreview";

/** Drafts with the specification's defaults; a specification that fails to draft gets no thumbnail. */
function draftDefaults(specification: PatternSpecification): DraftResult | undefined {
  try {
    const draft = draftPattern(specification, defaultsFor(specification));
    return draft.blocks.length > 0 ? draft : undefined;
  } catch {
    return undefined;
  }
}

/** Packs pieces to fill the card's shape (width / height), rather than the tall column they take on the printed sheets. */
function packBlocks(blocks: DraftBlock[], aspect: number) {
  const gap = Math.max(...blocks.map((block) => Math.max(block.bounds.width, block.bounds.height))) * 0.08;
  const packed = packBoxes(blocks.map((block) => block.bounds), aspect, gap);
  const placements = blocks.map((block, index) => ({ block, index, x: packed.positions[index].x - block.bounds.minX, y: packed.positions[index].y - block.bounds.minY }));
  return { placements, width: packed.width, height: packed.height };
}

/** Library card preview: every piece's cut line in its workspace colour. */
export function PatternThumbnail(props: { specification: PatternSpecification; aspect?: number }) {
  const draft = draftDefaults(props.specification);
  return (
    <div class="pattern-thumbnail">
      <Show when={draft} fallback={<span>No preview</span>}>
        {(result) => {
          const layout = packBlocks(result().blocks, props.aspect ?? 2);
          const pad = Math.max(layout.width, layout.height) * 0.03;
          return (
            <svg viewBox={`${-pad} ${-pad} ${layout.width + pad * 2} ${layout.height + pad * 2}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label={`${props.specification.pattern.name} pieces`}>
              <For each={layout.placements}>{(placement) => (
                <g transform={`translate(${placement.x} ${placement.y})`} stroke={pieceColourCss(placement.index)}>
                  <For each={result().edges.filter((edge) => placement.block.edgeIds.includes(edge.id))}>
                    {(edge) => <path d={edge.segments.map(segmentPath).join(" ")} fill="none" stroke-width="1.5" vector-effect="non-scaling-stroke" />}
                  </For>
                </g>
              )}</For>
            </svg>
          );
        }}
      </Show>
    </div>
  );
}
